import {
  ContentBlock,
  ContentState,
  Editor,
  EditorState,
  RawDraftContentBlock,
  RawDraftEntity,
} from "draft-js";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";

import { ServiceViewType } from "@/apis/clients";

// PERFORMANCE: Lazy-load templates to reduce initial bundle size
// Each template is loaded only when it's actually rendered
// This significantly reduces initial JavaScript payload

// Lazy-loaded templates - each loaded on-demand when rendered
const OverviewTemplate = lazy(() => import("@/components/editors//template-blocks/overview/OverviewTemplate").then(m => ({ default: m.OverviewTemplate })));
const BlockBorderTemplate = lazy(() => import("@/components/editors/template-blocks/block-border/BlockBorderTemplate"));
const BlockTemplate = lazy(() => import("@/components/editors/template-blocks/block/BlockTemplate"));
const ButtonWithoutServiceLinkTemplate = lazy(() => import("@/components/editors/template-blocks/button-without-service-link/ButtonWithoutServiceLinkTemplate"));
const ButtonLargeDoubleArrowTemplate = lazy(() => import("@/components/editors/template-blocks/button/ButtonLargeDoubleArrowTemplate").then(m => ({ default: m.ButtonLargeDoubleArrowTemplate })));
const ButtonServiceDefaultTemplate = lazy(() => import("@/components/editors/template-blocks/button/ButtonServiceDefaultTemplate").then(m => ({ default: m.ButtonServiceDefaultTemplate })));
const ChecklistTemplate = lazy(() => import("@/components/editors/template-blocks/checklist/ChecklistTemplate"));
const CtaButtonTemplate = lazy(() => import("@/components/editors/template-blocks/cta-button/CtaButtonTemplate"));
const FaqTemplate = lazy(() => import("@/components/editors/template-blocks/faq/FaqTemplate"));
const FormTemplate = lazy(() => import("@/components/editors/template-blocks/form/FormTemplate").then(m => ({ default: m.FormTemplate })));
const FrameTemplate = lazy(() => import("@/components/editors/template-blocks/frame/FrameTemplate"));
const HeaderTemplate = lazy(() => import("@/components/editors/template-blocks/header/HeaderTemplate"));
const HeadingTemplate = lazy(() => import("@/components/editors/template-blocks/heading/HeadingTemplate"));
const ServiceHeadingTemplate = lazy(() => import("@/components/editors/template-blocks/heading/ServiceHeadingTemplate"));
const ImageTemplate = lazy(() => import("@/components/editors/template-blocks/image/ImageTemplate"));
const LinkButtonLargeDoubleArrowTemplate = lazy(() => import("@/components/editors/template-blocks/link-button/LinkButtonLargeDoubleArrowTemplate"));
const LinkImageTemplate = lazy(() => import("@/components/editors/template-blocks/link-image/LinkImageTemplate"));
const LogoTemplate = lazy(() => import("@/components/editors/template-blocks/logo/LogoTemplate"));
const MessageTemplate = lazy(() => import("@/components/editors/template-blocks/message/MessageTemplate"));
const ProfileTemplate = lazy(() => import("@/components/editors/template-blocks/profile/ProfileTemplate"));
const ServiceFormTemplate = lazy(() => import("@/components/editors/template-blocks/service-form/ServiceFormTemplate").then(m => ({ default: m.ServiceFormTemplate })));
const ServiceMenuCalendarTemplate = lazy(() => import("@/components/editors/template-blocks/service-menu/ServiceMenuCalendarTemplate").then(m => ({ default: m.ServiceMenuCalendarTemplate })));
const ServiceMenuTemplate = lazy(() => import("@/components/editors/template-blocks/service-menu/ServiceMenuTemplate").then(m => ({ default: m.ServiceMenuTemplate })));
const ServiceTemplate = lazy(() => import("@/components/editors/template-blocks/service/ServiceTemplate"));

// Extract ServicePartsOptions type from the lazy-loaded module
type ServicePartsOptions = import("@/components/editors/template-blocks/service/ServiceTemplate").ServicePartsOptions;

const SliderImageTemplate = lazy(() => import("@/components/editors/template-blocks/slider/SliderImageTemplate").then(m => ({ default: m.SliderImageTemplate })));
const SliderMarketingTemplate = lazy(() => import("@/components/editors/template-blocks/slider/SliderMarketingTemplate").then(m => ({ default: m.SliderMarketingTemplate })));
const SnsButton1Template = lazy(() => import("@/components/editors/template-blocks/sns/SnsButton1Template"));
const SnsButtonTemplate = lazy(() => import("@/components/editors/template-blocks/sns/SnsButtonTemplate"));
const LineTemplate = lazy(() => import("@/components/editors/template-blocks/space-line/LineTemplate"));
const SpaceTemplate = lazy(() => import("@/components/editors/template-blocks/space-line/SpaceTemplate"));
const StepTemplate = lazy(() => import("@/components/editors/template-blocks/step/StepTemplate"));
const InstagramStyleLpTemplate = lazy(() => import("@/components/editors/template-blocks/style-lp/InstagramStyleLpTemplate"));
const TableTemplate = lazy(() => import("@/components/editors/template-blocks/table/TableTemplate").then(m => ({ default: m.TableTemplate })));
const TextTemplate = lazy(() => import("@/components/editors/template-blocks/text/TextTemplate"));
const VideoTemplate = lazy(() => import("@/components/editors/template-blocks/video/VideoTemplate"));
const TopHeaderTemplate = lazy(() => import("@/components/editors/template-blocks/top-header/TopHeaderTemplate"));
const TopTemplate = lazy(() => import("@/components/editors/template-blocks/top/TopTemplate"));
const CtaSnsTemplate = lazy(() => import("@/components/editors/template-blocks/cta-button/CtaSnsTemplate"));

// Fallback component for Suspense
const TemplateFallback = () => null;

import DraftStyles from "@/models/editors/draft-js-rte/draftjs.style";
import {
  customStyleFn,
  customStyleMap,
} from "@/models/editors/draft-js-rte/renderConfig";
import { fontFamilyOptions } from "@/models/editors/editor";
import { CtaButtonSnsTemplate, CtaButtonTypeTemplate } from "@/states/page-editor/template-parts/cta-button";
import { HeadingTypeTemplate } from "@/states/page-editor/template-parts/heading";
import { FormServiceTemplate } from "@/states/page-editor/template-parts/service-form";
import { ServiceMenuTemplate as IServiceMenuTemplate } from "@/states/page-editor/template-parts/service-menu";
import { TemplatePart } from "@/states/page-editor/template-parts/types";
import { useSubmitForm } from "@/hooks/useSubmitForm";

/**
 * ページに紐づくサービスとテンプレートのBlockKey
 */
export interface ConnectedService {
  serviceId: number;
  templateBlockKey: string;
}

// part.contentがContentStateになっていないので応急処置としてinterfaceとユーザ定義TypeGuardを利用
// TODO Page APIのサーバ繋ぎ込み時に再度確認し、この部分を不要にしたい
type contentType =
  | {
      entityMap: { [key: string]: RawDraftEntity };
      blockMap: { [key: string]: RawDraftContentBlock };
      hasText: undefined;
    }
  | ContentState;

interface TemplatePartWithContent {
  content: contentType | ContentState;
  table: Array<{
    content: contentType;
  }>;
  titleContent: {
    content: contentType;
  };
  buttonContent: {
    content: contentType;
  };
}

export const TEMPLATE_PART_BLOCK_KEY = "TEMPLATE";

interface CustomBlockProps {
  block: ContentBlock;
  blockProps: {
    part: TemplatePart & TemplatePartWithContent;
    options: EditorDisplayOptions;
    connectedService: ConnectedService | undefined;
  };
}

const CustomBlockComponent = ({ block, blockProps }: CustomBlockProps) => {
  const key = block.getKey();
  const { part, options, connectedService } = blockProps;

  return (
    <div style={{ fontFamily: fontFamilyOptions[0].value }}>
      <div>
        <TemplateRenderer
          id={key}
          part={part}
          options={options}
          connectedService={connectedService}
        />
      </div>
    </div>
  );
};

/**
 * カスタムブロックを描画する
 */
const blockRenderer = (
  contentBlock: ContentBlock,
  options: EditorDisplayOptions,
  connectedService: ConnectedService | undefined,
) => {
  if (contentBlock.getType() !== "atomic") {
    return;
  }

  const part = contentBlock.getData().get(TEMPLATE_PART_BLOCK_KEY) as
    | (TemplatePart & TemplatePartWithContent)
    | undefined;

  if (!part) {
    return;
  }

  if (
    part.type === "button_service_default" &&
    !part.link?.url &&
    !connectedService?.serviceId
  )
    return;

  return {
    component: CustomBlockComponent,
    editable: false,
    props: {
      part,
      options,
      connectedService,
    },
  };
};

export interface EditorDisplayOptions {
  partsProps: {
    buttonWithoutServiceLink?: {
      downloadUrl?: string;
    };
    buttonLargeDoubleArrow?: {
      formPath?: string;
    };
    buttonServiceDefault?: {
      formPath?: string;
    };
    formCompany?: {
      confirmPath?: string;
    };
    service?: ServicePartsOptions;

    ctaButtonService?: {
      formPath?: string;
      confirmPath?: string;
    };
    serviceViewType?: ServiceViewType;
    blockBorder?: {
      text?: string;
    };
  };
}

const defaultOptions: EditorDisplayOptions = {
  partsProps: {
    buttonWithoutServiceLink: {},
    blockBorder: {
      text: "",
    },
  },
};

interface Props {
  state: EditorState;
  options?: EditorDisplayOptions;
  connectedService?: ConnectedService;
}

export default function EditorDisplay({
  state,
  options,
  connectedService,
  ...other
}: Props) {
  const [editorState, setEditorState] = useState(state);
  const mergedOptions = options === undefined ? defaultOptions : options;
  const editorRef = useRef<Editor>(null);
  const onChange = useCallback(() => {
    // 何もしない
  }, []);

  // blockRendererFnが再描画されないため強制的に更新する
  useEffect(() => {
    setEditorState(
      EditorState.forceSelection(editorState, editorState.getSelection()),
    );
  }, [options]);

  return (
    // eslint-disable-next-line tailwindcss/no-custom-classname
    <DraftStyles className="rich-text-editor">
      <div {...other} className="leading-[1.5rem]">
        <Editor
          ref={editorRef}
          editorState={editorState}
          onChange={onChange}
          blockRendererFn={(component) =>
            blockRenderer(component, mergedOptions, connectedService)
          }
          blockStyleFn={blockStyleFn}
          customStyleMap={customStyleMap}
          customStyleFn={customStyleFn}
          readOnly={true}
        />
      </div>
    </DraftStyles>
  );
}

interface TemplateRendererProps {
  id: string;
  part: TemplatePart;
  options: EditorDisplayOptions;
  connectedService: ConnectedService | undefined;
}

function TemplateRenderer({
  part,
  options,
  connectedService,
}: TemplateRendererProps) {
  const { errors } = useSubmitForm();

  return (
    <Suspense fallback={<TemplateFallback />}>
      {(() => {
        switch (part.type) {
          case "logo":
            return <LogoTemplate part={part} />;
          case "header":
            return <HeaderTemplate part={part} />;
          case "header.1":
            return <HeaderTemplate part={part} />;
          case "top_header":
            return (
              <TopHeaderTemplate
                part={part}
              />
            );
          case "image":
          case "image.center":
          case "image.form":
          case "image.button":
            return (
              <ImageTemplate
                part={part}
                formPath={options.partsProps.ctaButtonService?.formPath}
                confirmPath={options.partsProps.ctaButtonService?.confirmPath}
              />
            );
          case "heading":
          case "heading.frame":
          case "heading.balloon":
          case "heading.arrow":
          case "heading.up_down":
          case "heading.accent":
          case "heading.large":
          case "heading.marker":
          case "heading.decoration.1":
          case "heading.decoration.2":
          case "heading.decoration.3":
          case "heading.quote":
          case "heading.balloon_fill":
          case "heading.ribbon":
            return <HeadingTemplate part={part} />;

          case "video":
          case "video.type1":
            return <VideoTemplate part={part} />;
          case "space":
          case "space.30":
          case "space.50":
          case "space.100":
            return <SpaceTemplate part={part} />;
          case "line":
          case "line.1":
          case "line.3":
          case "line.5":
            return <LineTemplate part={part} />;
          case "form.company":
          case "form.simple":
            return (
              <FormTemplate
                part={part}
                confirmPath={options.partsProps.formCompany?.confirmPath}
              />
            );

          case "service.calendar_reservation_free":
            return (
              <ServiceTemplate
                part={part}
                confirmPath={options.partsProps.formCompany?.confirmPath}
                connectedService={connectedService}
                servicePartsOptions={options.partsProps.service}
              />
            );
          case "sns":
            return <SnsButtonTemplate part={part} />;
          case "sns.type1":
            return <SnsButton1Template part={part} />;
          case "table.2_left_small":
          case "table.2":
          case "table.3":
          case "table.4":
            return <TableTemplate part={part} />;
          case "overview.service":
            return <OverviewTemplate part={part} />;
          case "overview.frame":
            return <FallbackTemplate part={part} />;
          case "slider.marketing":
            return <SliderMarketingTemplate part={part} />;
          case "slider.image":
            return <SliderImageTemplate part={part} />;
          case "button.large_double_arrow":
            return (
              <ButtonLargeDoubleArrowTemplate
                part={part}
                formPath={options.partsProps.buttonLargeDoubleArrow?.formPath}
                connectedService={connectedService}
              />
            );
          case "button_without_service_link":
            return (
              <ButtonWithoutServiceLinkTemplate
                part={part}
                downloadUrl={options.partsProps.buttonWithoutServiceLink?.downloadUrl}
              />
            );
          case "button_service_default":
            return (
              <ButtonServiceDefaultTemplate
                part={part}
                formPath={options.partsProps.buttonServiceDefault?.formPath}
                connectedService={connectedService}
              />
            );
          case "link_button.large_double_arrow":
            return <LinkButtonLargeDoubleArrowTemplate part={part} />;

          case "step.1":
          case "step.2":
          case "step.3":
          case "step.4":
            return <StepTemplate part={part} />;
          case "checklist.1":
          case "checklist.2":
          case "checklist.3":
          case "checklist.4":
            return <ChecklistTemplate part={part} />;
          case "faq.1":
          case "faq.2":
          case "faq.3":
            return <FaqTemplate part={part} />;
          case "profile.type1":
          case "profile.type2":
          case "profile.type3":
          case "profile.type4":
            return <ProfileTemplate part={part} />;
          case "cta_button.1":
          case "cta_button.2":
          case "cta_button.3":
          case "cta_button.4":
          case "cta_button.5":
            return (
              <CtaButtonTemplate
                part={part as CtaButtonTypeTemplate}
                formPath={options.partsProps.ctaButtonService?.formPath}
                confirmPath={options.partsProps.ctaButtonService?.confirmPath}
              />
            );
          case "cta_button.sns":
            return (
              <CtaSnsTemplate
                part={part as CtaButtonSnsTemplate}
              />
            );

          case "style_lp.instagram":
            return (
              <InstagramStyleLpTemplate
                part={part}
                formPath={options.partsProps.buttonServiceDefault?.formPath}
                connectedService={connectedService}
              />
            );

          case "message.type1":
          case "message.type2":
          case "message.type3":
            return <MessageTemplate part={part} />;

          case "block.1":
            return <BlockTemplate part={part} />;
          case "block.border":
            return (
              <BlockBorderTemplate
                text={options.partsProps.blockBorder?.text}
                part={part}
              />
            );

          case "link_image":
            return <LinkImageTemplate part={part} />;

          case "text":
            return <TextTemplate part={part} />;

          case "frame.1":
          case "frame.2":
          case "frame.3":
          case "frame.4":
            return <FrameTemplate part={part} />;

          case "form2.type1":
            return (
              <ServiceFormTemplate
                part={part}
                formPath={options.partsProps.ctaButtonService?.formPath}
              />
            );
          case "menu.calendar":
            return <ServiceMenuCalendarTemplate part={part} />;
          case "menu.eventSeminar":
          case "menu.form":
          case "menu.subscription":
            return (
              <>
                <ServiceMenuTemplate part={part as IServiceMenuTemplate} />
                {options.partsProps?.serviceViewType ===
                  ServiceViewType.FORM && (
                  <>
                    <ServiceHeadingTemplate
                      part={part as HeadingTypeTemplate}
                    />
                    <ServiceFormTemplate part={part as FormServiceTemplate} />
                  </>
                )}
                {/* {errors?.service.isErrorCapacity && errors?.service.isShowError && (
                  <div className="py-2 px-4 border border-solid border-[#FFE58F] bg-[#FFFBE6] text-xs rounded-lg flex gap-2 max-w-[620px] mx-auto">
                    <CausionCircle className="w-4 h-4 shrink-0" />
                    定員の残数に対して、チケット数が上限を超えました。お手数ですがチケット数を変更して申込みしてください。
                  </div>
                )} */}
                <CtaButtonTemplate
                  part={part as CtaButtonTypeTemplate}
                  formPath={options.partsProps.ctaButtonService?.formPath}
                  confirmPath={options.partsProps.ctaButtonService?.confirmPath}
                />
              </>
            );
          case "menu.other":
            return (
              <>
                <ServiceFormTemplate part={part as FormServiceTemplate} />
                <CtaButtonTemplate
                  part={part as CtaButtonTypeTemplate}
                  formPath={options.partsProps.ctaButtonService?.formPath}
                  confirmPath={options.partsProps.ctaButtonService?.confirmPath}
                />
              </>
            );
          case "top.1":
            return (
              <TopTemplate
                part={part}
              />
            );
          default:
            return <FallbackTemplate part={part} />;
        }
      })()}
    </Suspense>
  );
}

interface FallbackTemplateProps {
  part: TemplatePart;
}

/**
 * TODO: テンプレパーツコンポーネントを全て実装したら非表示に変えるなどする
 */
function FallbackTemplate({ part }: FallbackTemplateProps) {
  return <div>TODO: {part.type}は未実装です</div>;
}

/**
 * ブロック要素のスタイル(クラス名)を返却
 * @param block
 */
function blockStyleFn(block: ContentBlock): string {
  const classes: string[] = [];
  block.getData().forEach((v: string, k: string) => {
    switch (k) {
      case "text-align":
      case "float":
        classes.push(`${k}-${v}`);
        break;
      default:
        return null;
    }
  });
  return classes.length ? classes.join(" ") : "";
}
